import React, {useEffect, useState} from 'react'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {useLinkedIn} from 'react-linkedin-login-oauth2'
import {useGoogleLogin} from '@react-oauth/google'
import axios from 'axios'
import {useNavigate} from 'react-router-dom'
import {useAuth} from '../core/Auth'
import {useIntl} from 'react-intl'
import TagManager from 'react-gtm-module'
import {localeOptions} from '../../profile/core/_constants'
import {GTMEvent, loginSuccessMeta, emailVerificationMeta} from '../../../hooks/gtmHelpers'
import {getUserByToken} from '../core/_requests'
import {parseJsonString, removeNullFields} from '../../../core/_util'
import {getLocation} from '../../onboarding/core/_requests'

const SocialLoginButtons = ({
  disable,
  loading1,
  setLoading1,
}: {
  disable: boolean
  loading1: boolean
  setLoading1: any
}) => {
  const navigate = useNavigate()
  const {
    saveAuth,
    setUserToken,
    setNewCompany,
    storeCompanyId,
    setCompanyQueuePosition,
    setUseCase,
    setInvoiceStatus,
    setPlanStatus,
    setTwoFaToken,
  } = useAuth()
  const {formatMessage} = useIntl()
  const [loading, setLoading] = useState(false)
  // const [loading1, setLoading1] = useState(false)
  const [, setErrorsArray] = useState<string[]>()
  const IPSTACK_URL = `${process.env.REACT_APP_IPSTACK_LINK}?access_key=${process.env.REACT_APP_IPSTACK_ACCESS_KEY}&format=1`
  const [ipStackData, setIpStackData] = useState<any>()
  const localStorageUtmSource = localStorage.getItem('utm_source') || ''
  const localStorageUtmMedium = localStorage.getItem('utm_medium') || ''
  const localStorageUtmCampaign = localStorage.getItem('utm_campaign') || ''
  const localStorageReferral = localStorage.getItem('referral') || ''
  const localStorageUtmContent = localStorage.getItem('utm_content') || ''
  const localStorageUtmTerm = localStorage.getItem('utm_term') || ''
  const localStorageGclid = localStorage.getItem('rk_gclid') || ''
  const isRecordskeeper = process.env.REACT_APP_BRAND === 'RK'

  const formatUTCOffset = (dateString: string) => {
    if (!dateString) return null

    // Extract the GMT offset from the date string
    const gmtOffset = dateString.substring(dateString.lastIndexOf('+') + 1)

    // Format the UTC offset into the desired format (UTC+HH:mm)
    const sign = gmtOffset.startsWith('-') ? '-' : '+'
    const offsetParts = gmtOffset.split(':')
    const hours = offsetParts[0].replace(sign, '').padStart(2, '0')
    const minutes = offsetParts[1].padStart(2, '0')

    const formattedOffset = `UTC${sign}${hours}:${minutes}`
    return formattedOffset
  }

  useEffect(() => {
    const fetchIpStackData = async () => {
      try {
        const ipStackData = await getLocation()
        setIpStackData(ipStackData.data)
      } catch (err) {
        console.log(err)
      }
    }
    fetchIpStackData()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const googleLogin = useGoogleLogin({
    onSuccess: async (response) => {
      const locationApiData = await axios.get<any, any>(IPSTACK_URL)

      // Safely accessing the first language code or falling back to a default if not present
      const firstLanguageCode = locationApiData?.data?.location?.languages?.[0]?.code || 'en'

      // Determining the locale based on the language code
      const locale =
        localeOptions?.find((o: any) => o.value.split('_')[0] === firstLanguageCode)?.value ||
        'en_US'

      const {
        data: {data: googleData, errors, success},
      } = await axios.post(`${process.env.REACT_APP_BASE_API_URL}/auth/social-signin`, {
        type: 'GOOGLE',
        token: response.access_token,
        referral: localStorageReferral,
        dateFormat: 'MMM DD, YYYY',
        timeFormat: 'hh:mm:ss a',
        timeZone: formatUTCOffset(locationApiData?.data.time_zone?.current_time) || 'UTC-05:00',
        locale,
        countryCode: locationApiData?.data?.country_code || 'US',
        stateCode: locationApiData?.data?.region_code || 'NY',
        city: locationApiData?.data?.city || 'Manhattan',
        utm_source: localStorageUtmSource,
        utm_medium: localStorageUtmMedium,
        utm_campaign: localStorageUtmCampaign,
        utm_content: localStorageUtmContent,
        utm_term: localStorageUtmTerm,
        gclid: localStorageGclid,
      })

      if (success) {
        if (googleData?.twoFa) {
          setTwoFaToken(googleData.token)
          navigate('/auth/2fa-verification')
        } else {
          const {data} = await getUserByToken(googleData.token)

          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.EmailVerificationStartSuccessful,
              signup_method: 'Google',
              metadata: removeNullFields({
                email: data?.data?.email,
                utm_source: parseJsonString(localStorageUtmSource),
                utm_medium: parseJsonString(localStorageUtmMedium),
                utm_campaign: parseJsonString(localStorageUtmCampaign),
                utm_content: parseJsonString(localStorageUtmContent),
                utm_term: parseJsonString(localStorageUtmTerm),
                gclid: parseJsonString(localStorageGclid),
                country_name: ipStackData?.country_name || 'NA',
                region_name: ipStackData?.region_name || 'NA',
                city_name: ipStackData?.city || 'NA',
                postcode: ipStackData?.zip || 111111,
              }),
            },
          })
          if (googleData.isSignup) {
            setTimeout(() => {
              TagManager.dataLayer({
                dataLayer: {
                  event: GTMEvent.EmailVerificationSuccess,
                  signup_method: 'Google',
                  metadata: removeNullFields({
                    country_name: ipStackData?.country_name || 'NA',
                    region_name: ipStackData?.region_name || 'NA',
                    city_name: ipStackData?.city || 'NA',
                    postcode: ipStackData?.zip || 111111,
                    ...emailVerificationMeta(data.data, {
                      name: `${data.data.firstName}'s Company`,
                    }),
                  }),
                },
              })
            }, 100)

            setTimeout(() => {
              TagManager.dataLayer({
                dataLayer: {
                  event: GTMEvent.SignupSuccess,
                  signup_method: 'Google',
                  metadata: removeNullFields({...loginSuccessMeta(data.data)}),
                },
              })
            }, 150)
            localStorage.setItem('email', data.data.email)
          } else {
            setTimeout(() => {
              TagManager.dataLayer({
                dataLayer: {
                  event: GTMEvent.LoginSuccess,
                  signup_method: 'Google',
                  metadata: removeNullFields({
                    country_name: ipStackData?.country_name || 'NA',
                    region_name: ipStackData?.region_name || 'NA',
                    city_name: ipStackData?.city || 'NA',
                    postcode: ipStackData?.zip || 111111,
                    ...loginSuccessMeta(data.data),
                  }),
                },
              })
            }, 100)
          }

          setLoading1(false)
          await saveAuth(googleData)
          if (data?.data?.defaultCompanyId !== null && data?.data?.defaultCompanyId !== undefined) {
            const findCompany = data?.data?.company?.find(
              (company: {companyId: number}) => company.companyId === data?.data?.defaultCompanyId
            )
            if (findCompany) {
              storeCompanyId(data?.data?.defaultCompanyId)
              setCompanyQueuePosition(findCompany?.queuePosition)
              setUseCase(findCompany?.useCase)
              if (data?.data?.companyPlanStatus?.invoiceStatus) {
                setInvoiceStatus(data?.data?.companyPlanStatus?.invoiceStatus)
              }
              if (data?.data?.companyPlanStatus?.planStatus) {
                setPlanStatus(data?.data?.companyPlanStatus?.planStatus)
              }
            }
          }
          await saveAuth(googleData)
          setUserToken(googleData.token)
          // setUserToken(googleData.token)
          if (!googleData.companyPresent) {
            setNewCompany(false)
          }
          // navigate('/')
          if (
            (data?.data?.company?.length === 0 && isRecordskeeper) ||
            (!data?.data?.contact && isRecordskeeper) ||
            (!data?.data?.contactVerifiedAt && isRecordskeeper) ||
            (!data?.data?.countryDialingCode && isRecordskeeper)
          ) {
            navigate('/auth/info/')
          } else {
            navigate('/')
          }
        }
      } else {
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.LoginFailed,
            signup_method: 'Google',
            email: '',
            metadata: removeNullFields({
              country_name: ipStackData?.country_name || 'NA',
              region_name: ipStackData?.region_name || 'NA',
              city_name: ipStackData?.city || 'NA',
              postcode: ipStackData?.zip || 111111,
            }),
          },
        })
        setErrorsArray(errors)
        setLoading1(false)
        await saveAuth(undefined)
      }
    },
    onNonOAuthError: (error) => {
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.SignupInitiatedFailed,
          signup_method: 'Google',
          metadata: removeNullFields({
            country_name: ipStackData?.country_name || 'NA',
            region_name: ipStackData?.region_name || 'NA',
            city_name: ipStackData?.city || 'NA',
            postcode: ipStackData?.zip || 111111,
            error: error.type,
          }),
        },
      })
      setLoading1(false)
    },
    onError: (error) => {
      console.log('Google Login Error:', error)
      setLoading1(false)
    },
  })

  const {linkedInLogin} = useLinkedIn({
    clientId: process.env.REACT_APP_LINKEDIN_CLIENT_ID as string,
    redirectUri: `${window.location.origin}/auth/linkedin`,
    onSuccess: async (code) => {
      setLoading(true)
      const {
        data: {data: linkedinData, errors, success},
      } = await axios.post(`${process.env.REACT_APP_BASE_API_URL}/auth/social-signin`, {
        type: 'LINKEDIN',
        token: code,
        referral: localStorageReferral,
      })
      if (success) {
        const {data} = await getUserByToken(linkedinData.token)

        if (linkedinData.isSignup) {
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.SignupSuccess,
              signup_method: 'Linkedin',
              metadata: removeNullFields({...loginSuccessMeta(data.data)}),
            },
          })
        } else {
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.LoginSuccess,
              signup_method: 'Linkedin',
              metadata: removeNullFields({
                country_name: ipStackData?.country_name || 'NA',
                region_name: ipStackData?.region_name || 'NA',
                city_name: ipStackData?.city || 'NA',
                postcode: ipStackData?.zip || 111111,
                ...loginSuccessMeta(data.data),
              }),
            },
          })
        }

        setLoading(false)
        await saveAuth(linkedinData)

        if (data?.data?.defaultCompanyId !== null && data?.data?.defaultCompanyId !== undefined) {
          const findCompany = data?.data?.company?.find(
            (company: {companyId: number}) => company.companyId === data?.data?.defaultCompanyId
          )
          if (findCompany) {
            storeCompanyId(data?.data?.defaultCompanyId)
          }
        }
        setUserToken(linkedinData.token)
        if (!linkedinData.companyPresent) {
          setNewCompany(false)
        }
        navigate('/')
      } else {
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.LoginFailed,
            signup_method: 'Linkedin',
            email: '',
            metadata: removeNullFields({
              country_name: ipStackData?.country_name || 'NA',
              region_name: ipStackData?.region_name || 'NA',
              city_name: ipStackData?.city || 'NA',
              postcode: ipStackData?.zip || 111111,
            }),
          },
        })
        setLoading(false)
        setErrorsArray(errors)
        await saveAuth(undefined)
      }
    },
    scope: 'r_emailaddress r_liteprofile',
    onError: (error) => {
      console.log(error.errorMessage)
    },
  })

  return (
    <>
      {/* begin::Col */}
      <div className='col-12'>
        <button
          type='button'
          onClick={() => {
            setLoading1(true)

            TagManager.dataLayer({
              dataLayer: {
                event: GTMEvent.SignupInitiatedSuccessful,
                signup_method: 'Google',
                metadata: removeNullFields({
                  utm_source: parseJsonString(localStorageUtmSource),
                  utm_medium: parseJsonString(localStorageUtmMedium),
                  utm_campaign: parseJsonString(localStorageUtmCampaign),
                  utm_content: parseJsonString(localStorageUtmContent),
                  utm_term: parseJsonString(localStorageUtmTerm),
                  gclid: parseJsonString(localStorageGclid),
                }),
              },
            })

            googleLogin()
          }}
          className='btn btn-flex btn-light-primary flex-center text-nowrap py-3 px-5 w-100 h-54px'
          disabled={disable || loading1}
        >
          <img
            alt='Logo'
            src={toAbsoluteUrl('/media/svg/brand-logos/google-icon.svg')}
            className='h-15px google-signin-button me-3'
          />
          {!loading1 && (
            <span className='indicator-label'>{formatMessage({id: 'Continue with Google'})}</span>
          )}
          {loading1 && (
            <span className='indicator-progress' style={{display: 'block'}}>
              {formatMessage({id: 'Please wait...'})}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Col */}

      {/* begin::Col */}
      <div className='col-12 d-none'>
        {/* begin::Google link */}
        <button
          type='button'
          onClick={linkedInLogin}
          className='btn btn-linkedin py-3 px-5 w-100 mt-4 mb-10 mb-md-5  h-md-54px'
        >
          <img
            alt='Logo'
            src={toAbsoluteUrl('/media/svg/brand-logos/linkedin-2.svg')}
            className='theme-light-show h-20px me-3'
          />
          <img
            alt='Logo'
            src={toAbsoluteUrl('/media/svg/brand-logos/linkedin-2.svg')}
            className='theme-dark-show h-15px me-3'
          />
          {!loading && (
            <span className='indicator-label'>{formatMessage({id: 'Continue with LinkedIn'})}</span>
          )}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              {formatMessage({id: 'Please wait...'})}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        {/* end::Google link */}
      </div>
      {/* end::Col */}
    </>
  )
}

export default SocialLoginButtons
